declare var $;

import { AccordionToggle } from '../tv_utils/accordion_toggle';
import { SimplePaymentView } from '../tv_payments/payments';
import { CancelOrderSection } from './cancel_order_section';
import { PaymentSection } from './payment_section';
import { DIFUUploadQueue } from '../tv_artwork/difu_upload_queue';

export class CustomerOrderSearch {
    /**
     * @var {MutationObserver} observer for pagination contents
     * @var {{[key: string]: (para: MouseEvent) => void}} bound_handle bound event handlers
     * @var {boolean} loading_order_status is set if we have a load pending
     * @var {number} page being displayed
     */
    observer: MutationObserver;
    bound_handler: {[key: string]: (para: MouseEvent) => void} = {};
    loading_order_status: boolean = false;
    page: number = 1;

    /**
     * Bind the controls needed for the view.
     *
     * @returns {CustomerOrderSearch} with controls bound.
     */
    constructor() {
        this.bound_handler.pay_now = this.pay_now.bind(this);
        this.bound_handler.pagination_click = this.pagination_click.bind(this);
        this.observer = new MutationObserver(
            (mutationsList, observer) => {
                for (const mutation of mutationsList) {
                    const mutation_id = mutation.target['id'];
                    // Ignore mutations related to pay now modal.
                    if (mutation_id && (mutation_id === 'js-selected-cc' || mutation_id.startsWith('js-pay-now'))) {
                        return;
                    }
                }
                this.load_order_status();
            }
        );
        this.observer.observe(
            document.getElementById('pagination_contents'),
            {
                subtree: true,
                childList: true
            }
        );
        this.load_order_status();
    }

    /**
     * Attach events and map references to the DOM.
     *
     * @returns {void}
     */
    bind() : void {
        const pay_now_links = document.getElementsByClassName('js-pay-now-link');
        for (const link of pay_now_links) {
            link.removeEventListener('click', this.bound_handler.pay_now);
            link.addEventListener('click', this.bound_handler.pay_now);
        }
        const pagination_links = document.getElementsByClassName('ty-pagination__item');
        for (const link of pagination_links) {
            link.removeEventListener('click', this.bound_handler.pagination_click);
            link.addEventListener('click', this.bound_handler.pagination_click);
        }
        this.handle_mobile_table_cells();

    }

    /**
     * Handle when the user clicks pay now.
     *
     * @param event {MouseEvent} the associated mouse event with the pay now button click.
     * @returns {void}
     */
    pay_now(event: MouseEvent) : void {
        const element = <HTMLElement>event.currentTarget;
        const sales_id = element.dataset.id;
        const url = `index.php?dispatch=tv_payments.pay_now&sales_id=${sales_id}`;
        const target_id = `js-pay-now-popup-${sales_id}`;
        document.getElementById(target_id).innerHTML = '';
        $.ceAjax('request', url, {
            method: 'GET',
            cache: false,
            result_ids: target_id,
            callback: () => {
                new SimplePaymentView(
                    {
                        'cc_saved': true,
                        'cc_new': true,
                        'net_terms': true,
                        'pay_later': false,
                        'paypal': true
                    },
                    target_id,
                    false,
                    this.hide_pay_now_button.bind(this, sales_id),
                    true
                );
            },
            data: { sales_id: sales_id }
        });
    }

    /**
     * Handle the mobile table displays for customer orders.
     * 
     * @returns {void}
     */
    handle_mobile_table_cells() : void {
        if (window.innerWidth <= 767) {
            const order_table_cells = document.getElementsByClassName('js-order-table-cell');

            for (const order_table_cell of order_table_cells) {
                if (!order_table_cell.innerHTML.trim()) {
                    order_table_cell.classList.add('tv-display__none');
                }
            }
        }
    }

    /**
     * Hides the pay now button for specified sales_id.
     *
     * @param sales_id of the button to hide.
     * @returns {void}
     */
    hide_pay_now_button(sales_id: string) {
        if (document.getElementById(`opener_pay_now-${sales_id}`)) {
            document.getElementById(`opener_pay_now-${sales_id}`).classList.add('tv-display__none');
        }
    }

    /**
     * Show spinner when pagination link is clicked
     *
     * @param event {MouseEvent} the associated mouse event with the pagination link click.
     * @returns {void}
     */
    pagination_click(event: MouseEvent) : void {
        $.toggleStatusBox('show', {
            overlay: true,
            statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Loading</span>',
            statusClass: 'ty-ajax-loading-box_text_block'
        });

        this.page = parseInt((event.target as HTMLElement).dataset.caPage, 10);
    }

    /**
     * Reloads order table if needed so order status is displayed and binds controls
     *
     * @returns {void}
     */
     load_order_status(): void {
        const request = document.getElementById('js-orders-request') as HTMLInputElement;
        if (this.loading_order_status || !request) {
            this.bind();
            return;
        }

        const params = new URLSearchParams(JSON.parse(request.value));
        if (parseInt(params.get('page'), 10) === this.page) {
            $.toggleStatusBox('hide');
        }

        if (params.has('load_order_status')) {
            this.bind();
            return;
        }

        params.set('dispatch', 'orders.search');
        params.set('load_order_status', '1');
        const url = 'index.php?' + params.toString();
        this.loading_order_status = true;

        $.ceAjax('request', url, {
            method: 'GET',
            cache: false,
            hidden: true,
            result_ids: 'orders-layout-table',
            callback: this.load_order_status_callback.bind(this)
       });
    }

    load_order_status_callback() {
        this.bind();
        this.loading_order_status = false;
    }
}

/**
 * Class to handle the disabling of butting and click events for the customer order details.
 */
export class CustomerOrderDetails {
    /**
     * @var {string} sales_id of the order.
     * @var {Dictionary} of the DOM elements.
     * @var {PaymentSection} pay_now_section containing payment status and controls.
     * @var {CancelOrderSection} cancel_order_section containing the order cancel button and status.
     */
    sales_id: string;
    elements: {[key: string]: HTMLElement | AccordionToggle};
    pay_now_section: PaymentSection;
    cancel_order_section: CancelOrderSection;
    loading_product_detail_summary: {[key: number]: boolean} = {};

    /**
     * Bind the controls needed for the view, and start the AJAX components.
     *
     * @returns {CustomerOrderDetails} with controls bound, and AJAX requests running.
     */
    constructor() {
        this.sales_id = document.getElementById('sales_id').innerText;
        const js_order_status_value = (<HTMLInputElement>document.getElementById('js-order-status-value'))?.value;
        const js_order_status = document.getElementById('js-order-status');
        const js_cancelled_order_notification = document.getElementById('js-cancelled-order-notification');
        const tv_order_details_shipping_header = document.getElementById('tv-order-details-shipping-header');
        const js_shipping_cancelled_span = document.getElementById('js-shipping-cancelled-span');

        this.elements = {
            ajax_toggles: undefined,
            image_toggles: undefined
        };

        this.fetch_artwork_cutoff_times();
        this.bind();

        // If order is cancelled, don't execute payment and cancel sections
        if (js_order_status_value != '4') {
            this.cancel_order_section = new CancelOrderSection(this.sales_id);
            this.pay_now_section = new PaymentSection(this.sales_id);
        }

        // Ensure cancelled headings and text display properly
        if (js_order_status_value == '4') {
            js_order_status.classList.add('tv-color__red');
            js_cancelled_order_notification.classList.remove('hidden');
            js_cancelled_order_notification.classList.add('alert-success');
            js_shipping_cancelled_span.innerHTML = "Cancelled";
        }
    }

    /**
     * Attach events and map references to the DOM.
     *
     * @returns {void}
     */
    bind() : void {
        const proof_links = document.getElementsByClassName('js-proof-link');
        for (const link of proof_links) {
            link.addEventListener('click', this.link_clicked.bind(this), false);
        }

        const image_toggles = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName('tv-accordion-toggle');
        for (const toggle of image_toggles) {
            // TODO CSCART4 -- Set instead of add?
            this.elements.image_toggles = new AccordionToggle(toggle);
        }

        const approval_buttons = document.getElementsByClassName('js-artwork-approval-button');
        for (const approve of approval_buttons) {
            approve.addEventListener('click', this.approve_artwork.bind(this), false);
        }

        this.load_product_detail_summary = this.load_product_detail_summary.bind(this);
        const config_summary_links = document.getElementsByClassName('js-config-summary-detail');
        for (const summary of config_summary_links) {
            summary.addEventListener('mouseover', this.load_product_detail_summary);
            summary.addEventListener('click', this.load_product_detail_summary);
        }

        const shipping_toggles = document.getElementsByClassName('js-shipping-details-toggle');
        for (const toggle of shipping_toggles) {
            toggle.addEventListener('click', this.toggle_shipping_details.bind(this))
        }

        const shipping_sections = document.getElementsByClassName('js-shipping-summary');

        for (const section of shipping_sections) {
            const section_element = <HTMLElement>section;
            const delivery_mode = section_element.dataset.dlvmode;
            if (delivery_mode === 'ESTERROR') {
                continue;
            }
            const sales_id = section_element.dataset.id;
            const delivery_address = JSON.parse(section_element.dataset.deliveryaddress);
            const invent_trans_id = section_element.dataset.inventtrans;
            const box_id = section_element.dataset.boxid;
            const dod_element = <HTMLInputElement>document.getElementById(`js-shipping-dod-${invent_trans_id}`);
            const dod = dod_element.value;
            const element_id = section_element.id;
            const order_status = (<HTMLInputElement>document.getElementById('js-order-status-value')).value;

            if (order_status != '4') {
                this.fetch_shipping_summary(
                    sales_id,
                    invent_trans_id,
                    delivery_address,
                    element_id,
                    dod,
                    parseInt(box_id, 10),
                    delivery_mode
                );
            }
        }

        const cell_contents = document.getElementsByClassName('ty-table__responsive-content');
        for (const cell of cell_contents) {
            this.hide_empty_cells(cell);
        }
        

        // Bind Design It For You.
        const difu_uploader_ids = document.getElementsByClassName('tv_uploader_ids');
        for (const uploader_id of difu_uploader_ids) {
            const difu_id = <HTMLInputElement>uploader_id;
            const difu_element = <HTMLElement>document.getElementById(`js-difu-invent-trans-id-${difu_id.value}`);
            const difu_opener = <HTMLElement>document.getElementsByClassName(`js-design-it-for-you-modal-opener-${difu_id.value}`)[0];
            const sales_id = difu_element.dataset.salesid;

            // This needs an uploader_id, and sales_id.
            (() => new DIFUUploadQueue(difu_id.value, sales_id))();
            difu_opener.classList.remove('tv-button__disabled');
        }
    }

    /**
     * Make AJAX request to approve the artwork.
     *
     * @param {Event} e button click event
     * @returns {void}
     */
    approve_artwork(e: Event) : void {
        let caller = <HTMLElement> e.target || <HTMLElement> e.srcElement;

        if (caller.tagName !== 'A') {
            caller = caller.querySelector('a');
        }

        const invent_id = caller.dataset.invent_id;

        const url = 'index.php?dispatch=tv_artwork.approve';

        $.ceAjax('request', url, {
            method: 'POST',
            cache: false,
            data: { inventtrans_id: invent_id },
            callback: () => {
                location.reload();
            }
        });
    }

    /**
     * Fetch and fill the shipping summary section.
     *
     * @param sales_id {string} The sales ID of the shipping line.
     * @param invent_trans_id {string}
     * @param delivery_address {object}
     * @param element_id {string}
     * @param dod {string}
     * @param box_id {number}
     * @param delivery_mode {string}
     * @returns {void}
     */
    fetch_shipping_summary(
        sales_id: string,
        invent_trans_id: string,
        delivery_address: object,
        element_id: string,
        dod: string,
        box_id: number,
        delivery_mode: string
    ) : void {
        $.ceAjax('request', 'index.php?dispatch=tv_shipping.delivery_info', {
            method: 'POST',
            cache: false,
            hidden: true,
            result_ids: element_id,
            callback: null,
            data: {
                sales_id: sales_id,
                invent_trans_id: invent_trans_id,
                delivery_address: delivery_address,
                dod: dod,
                box_id: box_id,
                delivery_mode: delivery_mode
            }
        });
    }

    /**
     * Hide the empty cells inside of mobile order tables.
     * 
     * @oaram cell to grab the table cells off the page.
     * @returns {void}
     */
    hide_empty_cells(cell:Element) : void {
        if (cell.childNodes.length === 1 && cell.innerText === "" && window.innerWidth <= 767) {
            cell.parentElement.classList.add('tv-display__none');
        }
    }

    /**
     * Handle toggling a shipping detail section by mouse click.
     *
     * @param event {MouseEvent} The mouse event for the click.
     * @returns {void}
     */
    toggle_shipping_details(event: MouseEvent) : void {
        const target = <HTMLElement>event.currentTarget;
        const target_element_id = target.dataset.target;
        const element = document.getElementById(target_element_id);
        if (element.classList.contains('hidden')) {
            element.classList.remove('hidden');
        } else {
            element.classList.add('hidden');
        }
    }

    /**
     * Handle loading a product detail dialog on mouseover or click.
     * @param event {MouseEvent} The mouse event for the hover
     */
    load_product_detail_summary(event: MouseEvent) : void {
        const target = <HTMLElement>event.currentTarget;

        if (event.type === 'click') {
            $.toggleStatusBox('show');
        }

        if (this.loading_product_detail_summary[target.dataset.recId]) {
            return;
        }
        this.loading_product_detail_summary[target.dataset.recId] = true;
        
        $.ceAjax('request', 'index.php?dispatch=tv_customer_orders.config_summary', {
            method: 'POST',
            cache: false,
            hidden: true,
            result_ids: `js-config-summary-${target.dataset.recId}`,
            data: {record_id: target.dataset.recId, id: target.dataset.id },
            callback: () => {
                delete this.loading_product_detail_summary[target.dataset.recId];
                $.toggleStatusBox('hide');
                target.removeEventListener('mouseover', this.load_product_detail_summary);
                target.removeEventListener('click', this.load_product_detail_summary);
            }
        });
    }

    /**
     * Performs the AJAX requests to calculate artwork cutoff times for all of the artwork lines.
     *
     * @returns {void}
     */
    fetch_artwork_cutoff_times() : void {
        const lines = <HTMLCollectionOf<HTMLElement>> document.getElementsByClassName('js-artwork-cutoff-block');
        for (const line of lines) {
            if (line.dataset.code) {
                $.ceAjax('request', 'index.php?dispatch=tv_customer_orders.artwork_cutoff_time', {
                    method: 'POST',
                    caching: true,
                    hidden: true,
                    result_ids: line.id,
                    callback: null,
                    data: { code: line.dataset.code }
                });
            }
        }
    }

    /**
     * Event listener to handle clicks on the proof buttons to display the view proofs pop-up.
     *
     * @listens click
     * @param {Event} e from click event.
     * @returns {void}
     */
    link_clicked(e:Event) : void {
        let caller = <HTMLElement> e.target || <HTMLElement> e.srcElement;

        const id = caller.dataset.id;

        const url = `index.php?dispatch=tv_customer_orders.view_proofs_popup&invent_id=${id}`;

        $.ceAjax('request', url, {
            method: 'GET',
            cache: false,
            result_ids: 'proofs-popup',
            callback: () => {
                let show_older_btn = document.getElementById('js-show-older-revisions');
                let hide_older_btn = document.getElementById('js-hide-older-revisions');
                if (show_older_btn && hide_older_btn) {
                    this.show_or_hide_btns(true, show_older_btn, hide_older_btn);
                    this.show_or_hide_btns(false, hide_older_btn, show_older_btn);
                }
            }
        });
    }

    /**
     * Event handler for the show and hide old revision buttons.
     * When clicked, the button shows or hides older revisions, then
     * hides itself and shows its counterpart button.
     *
     * @param show boolean to tell what we're doing with the older revision rows.
     * @param this_btn this is the button that is getting the event listener.
     * @param that_btn this is the button that is being swapped out with the other.
     */
    show_or_hide_btns(show:boolean, this_btn:Element, that_btn:Element) : void {
        const row_display = show ? "table-row" : "none";
        this_btn.addEventListener('click', () => {
            const hidden_rows = <NodeListOf<HTMLElement>>document.querySelectorAll(
                '#order_details_proofs_list .js-hide-revision'
            );
            for (let row of hidden_rows) {
                row.style.display = row_display;
            }
            that_btn.classList.remove('tv-display__none');
            this_btn.classList.add('tv-display__none');
        });
    }
}
